import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  EmployeeAvatar,
  EmptyState,
  Icon,
  Spinner,
  Stat,
} from "../../design-system";
import {
  getAbsences,
  getEmployees,
  getInternalDocuments,
  getJornadaLaboral,
  getOvertimeRequests,
  getWorkspaceTodayStatus,
} from "../../helpers/kronaly_helper";
import useCurrentMembership from "../../helpers/useCurrentMembership";
import { useStartTour } from "../../tours/TourContext";

const ABSENCE_TYPE_LABEL = {
  vacation: "Vacaciones",
  personal: "Asunto propio",
  sick: "Baja médica",
  marriage: "Matrimonio",
  maternity: "Maternidad",
  paternity: "Paternidad",
  bereavement: "Fallecimiento familiar",
  moving: "Mudanza",
  study: "Estudios",
  other: "Otro",
};

const MONTH_SHORT = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
function formatDateRange(startYmd, endYmd) {
  if (!startYmd) return "";
  const [sy, sm, sd] = startYmd.split("-").map((n) => parseInt(n, 10));
  if (!endYmd || startYmd === endYmd) return `${sd} ${MONTH_SHORT[sm - 1]}`;
  const [ey, em, ed] = endYmd.split("-").map((n) => parseInt(n, 10));
  if (sm === em && sy === ey) return `${sd}–${ed} ${MONTH_SHORT[sm - 1]}`;
  return `${sd} ${MONTH_SHORT[sm - 1]} – ${ed} ${MONTH_SHORT[em - 1]}`;
}
function formatMinutes(min) {
  if (!min) return "0 min";
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}

/**
 * Hook centralizado de carga del dashboard. Hace los fetches en paralelo
 * y devuelve los derivados que consumen las tarjetas. Mantiene los datos
 * en un único lugar para evitar fetches duplicados entre cards.
 */
function useDashboardData(isManager) {
  const [state, setState] = useState({
    loading: true,
    employees: [],
    todayStatus: {}, // { [employee_profile_id]: { is_active, total_seconds } }
    absencesToday: [], // approved hoy
    pendingAbsences: [],
    pendingOvertime: [],
    pendingReadings: [], // docs internos con my_acknowledgement.status === pending
  });

  useEffect(() => {
    let cancelled = false;
    const today = todayYmd();
    (async () => {
      const tasks = [
        getEmployees().catch(() => ({ data: [] })),
        getWorkspaceTodayStatus().catch(() => ({ data: {} })),
        // approved absences que solapan con hoy — el backend no tiene filtro
        // por fecha, así que pedimos todos los approved y filtramos en cliente.
        getAbsences({ status: "approved" }).catch(() => ({ data: [] })),
        getInternalDocuments().catch(() => ({ data: [] })),
      ];
      if (isManager) {
        tasks.push(getAbsences({ status: "pending" }).catch(() => ({ data: [] })));
        tasks.push(getOvertimeRequests({ status: "pending" }).catch(() => ({ data: [] })));
      }
      const results = await Promise.all(tasks);
      if (cancelled) return;

      const employees = unwrap(results[0]) || [];
      const todayStatus = unwrap(results[1]) || {};
      const approved = unwrap(results[2]) || [];
      const internalDocs = unwrap(results[3]) || [];
      const pendingAbsences = isManager ? (unwrap(results[4]) || []) : [];
      const pendingOvertime = isManager ? (unwrap(results[5]) || []) : [];

      const absencesToday = approved.filter(
        (a) => a.start_date <= today && a.end_date >= today,
      );
      const pendingReadings = internalDocs.filter(
        (d) => d.requires_acknowledgement && d?.my_acknowledgement?.status === "pending",
      );

      setState({
        loading: false,
        employees,
        todayStatus,
        absencesToday,
        pendingAbsences,
        pendingOvertime,
        pendingReadings,
      });
    })();
    return () => { cancelled = true; };
  }, [isManager]);

  return state;
}

function Dashboard() {
  document.title = "Dashboard | Kronaly";
  const { me, isManager } = useCurrentMembership();
  const data = useDashboardData(isManager);
  // Tour: arranca tras la primera carga para que los anchors ya existan.
  useStartTour("dashboard", { enabled: !data.loading });
  return (
    <div className="kr-page">
      <DashboardHeader
        me={me}
        pendingCount={data.pendingAbsences.length + data.pendingOvertime.length}
        pendingReadingsCount={data.pendingReadings.length}
        isManager={isManager}
        loading={data.loading}
      />
      <div data-tour="dashboard-kpis">
        <KpiRow
          isManager={isManager}
          loading={data.loading}
          employees={data.employees}
          todayStatus={data.todayStatus}
          absencesToday={data.absencesToday}
          pendingCount={data.pendingAbsences.length + data.pendingOvertime.length}
        />
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.6fr 1fr",
          gap: 16,
          alignItems: "start",
        }}
      >
        <div data-tour="dashboard-activity">
          <ActivityChartCard />
        </div>
        <PendingRequestsCard
          isManager={isManager}
          loading={data.loading}
          pendingAbsences={data.pendingAbsences}
          pendingOvertime={data.pendingOvertime}
          employees={data.employees}
        />
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
          alignItems: "start",
        }}
      >
        <TodayInOfficeCard
          loading={data.loading}
          employees={data.employees}
          todayStatus={data.todayStatus}
          absencesToday={data.absencesToday}
        />
        <div data-tour="dashboard-readings">
          <PendingReadingsCard
            loading={data.loading}
            docs={data.pendingReadings}
          />
        </div>
      </div>
    </div>
  );
}

function DashboardHeader({ me, pendingCount, pendingReadingsCount, isManager, loading }) {
  const navigate = useNavigate();
  const firstName = (me?.name || "").trim().split(/\s+/)[0] || me?.name || "";
  const today = useMemo(() => formatLongDate(new Date()), []);

  // Mensaje contextual: manager habla de aprobaciones; empleado habla de
  // sus lecturas pendientes. Si no hay nada que destacar, mensaje neutro.
  const summary = loading
    ? "Cargando resumen…"
    : isManager
      ? pendingCount > 0
        ? `Tienes ${pendingCount} ${pendingCount === 1 ? "solicitud pendiente" : "solicitudes pendientes"} por revisar.`
        : "Todo está al día. No hay solicitudes pendientes."
      : pendingReadingsCount > 0
        ? `Tienes ${pendingReadingsCount} ${pendingReadingsCount === 1 ? "lectura obligatoria pendiente" : "lecturas obligatorias pendientes"}.`
        : "Bienvenido. No tienes pendientes ahora mismo.";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
        gap: 16,
        flexWrap: "wrap",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 11,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: "var(--kr-fg-muted)",
            fontWeight: 600,
            marginBottom: 4,
          }}
        >
          Resumen general · {today}
        </div>
        <h1
          style={{
            margin: 0,
            fontSize: 28,
            fontWeight: 600,
            letterSpacing: "-0.02em",
            color: "var(--kr-fg-strong)",
          }}
        >
          {firstName ? `Hola, ${firstName}` : "Hola"}
        </h1>
        <p style={{ margin: "6px 0 0", fontSize: 14, color: "var(--kr-fg-muted)" }}>
          {summary}
        </p>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {isManager && (
          <Button
            variant="secondary"
            icon="TrendingUp"
            onClick={() => navigate("/reportes")}
          >
            Reportes
          </Button>
        )}
        <Button
          variant="primary"
          icon="Plus"
          onClick={() => navigate("/ausencias")}
        >
          Nueva solicitud
        </Button>
      </div>
    </div>
  );
}

const LONG_MONTHS_ES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

function formatLongDate(d) {
  return `${d.getDate()} ${LONG_MONTHS_ES[d.getMonth()]} ${d.getFullYear()}`;
}

function KpiRow({ isManager, loading, employees, todayStatus, absencesToday, pendingCount }) {
  const activeEmployees = useMemo(
    () => employees.filter((e) => (e.status || "active") === "active"),
    [employees],
  );
  const workingNow = useMemo(
    () => Object.values(todayStatus).filter((s) => s?.is_active).length,
    [todayStatus],
  );

  const absenceHint = useMemo(() => {
    if (absencesToday.length === 0) return "Sin ausencias programadas";
    const byType = absencesToday.reduce((acc, a) => {
      acc[a.type] = (acc[a.type] || 0) + 1;
      return acc;
    }, {});
    const parts = [];
    if (byType.vacation) parts.push(`${byType.vacation} vacaciones`);
    if (byType.personal) parts.push(`${byType.personal} permisos`);
    if (byType.sick) parts.push(`${byType.sick} baja${byType.sick === 1 ? "" : "s"}`);
    if (parts.length === 0) parts.push(`${absencesToday.length} ausencias`);
    return parts.join(" · ");
  }, [absencesToday]);

  const valueOrDash = (v) => (loading ? "…" : String(v));

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: 14,
      }}
    >
      <Stat
        label="Trabajando ahora"
        value={valueOrDash(workingNow)}
        unit={!loading && activeEmployees.length > 0 ? `/ ${activeEmployees.length}` : undefined}
        icon="Clock"
      />
      <Stat
        label="Ausencias hoy"
        value={valueOrDash(absencesToday.length)}
        hint={!loading ? absenceHint : undefined}
        icon="Calendar"
      />
      <Stat
        label={isManager ? "Solicitudes pendientes" : "Empleados activos"}
        value={valueOrDash(isManager ? pendingCount : activeEmployees.length)}
        icon={isManager ? "Inbox" : "Users"}
      />
      <Stat
        label="Cumplimiento horario"
        value="—"
        hint="Disponible próximamente"
        icon="TrendingUp"
      />
    </div>
  );
}

const WEEKDAY_LABELS = ["L", "M", "X", "J", "V", "S", "D"];

function isoWeekNumber(ymd) {
  if (!ymd) return null;
  const [y, m, d] = ymd.split("-").map((n) => parseInt(n, 10));
  const date = new Date(Date.UTC(y, m - 1, d));
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
}

function fmtHours(min) {
  if (!min || min <= 0) return "0 h";
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (m === 0) return `${h.toLocaleString("es-ES")} h`;
  return `${h.toLocaleString("es-ES")} h ${m} min`;
}

const unwrap = (r) => {
  const inner = r?.data ?? r;
  return inner?.data ?? inner;
};

function todayYmd() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function ActivityChartCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getJornadaLaboral({ view: "week", date: todayYmd() });
        if (!cancelled) setData(unwrap(r));
      } catch (_) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const todayStr = todayYmd();

  const days = useMemo(() => {
    const apiDays = data?.days || [];
    return apiDays.slice(0, 7).map((d, i) => ({
      label: WEEKDAY_LABELS[i] || "?",
      date: d.date,
      worked: d.worked_minutes || 0,
      planned: d.planned_minutes || 0,
      isFuture: d.date > todayStr,
    }));
  }, [data, todayStr]);

  const scale = useMemo(() => {
    let max = 0;
    for (const d of days) {
      if (d.worked > max) max = d.worked;
      if (d.planned > max) max = d.planned;
    }
    return max || 480; // 8h default
  }, [days]);

  const totalsWorked = data?.totals?.worked_minutes || 0;
  const weekNumber = isoWeekNumber(data?.start_date);

  return (
    <Card>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 14,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "var(--kr-fg-muted)",
              fontWeight: 600,
            }}
          >
            Actividad semanal
          </div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: "var(--kr-fg-strong)",
              marginTop: 4,
            }}
          >
            {fmtHours(totalsWorked)} trabajadas
            {weekNumber && (
              <span
                style={{
                  fontSize: 12,
                  color: "var(--kr-fg-muted)",
                  fontWeight: 400,
                }}
              >
                {" "}· semana {weekNumber}
              </span>
            )}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: 14,
            fontSize: 11,
            color: "var(--kr-fg-muted)",
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                width: 8,
                height: 8,
                background: "var(--kr-action)",
                borderRadius: 2,
              }}
            />
            Trabajadas
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                width: 8,
                height: 8,
                background: "var(--kr-neutral-200)",
                borderRadius: 2,
              }}
            />
            Esperadas
          </span>
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 10,
          alignItems: "stretch",
          height: 180,
          padding: "0 4px",
          position: "relative",
        }}
      >
        {loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Spinner size={16} />
          </div>
        )}
        {(loading ? Array.from({ length: 7 }).map((_, i) => ({ label: WEEKDAY_LABELS[i], worked: 0, planned: 0, isFuture: false, date: null })) : days).map((d, i) => {
          const plannedPct = scale > 0 ? Math.min(100, (d.planned / scale) * 100) : 0;
          const workedPct = scale > 0 ? Math.min(100, (d.worked / scale) * 100) : 0;
          const tooltip = d.date
            ? `${d.date}: ${fmtHours(d.worked)} / ${fmtHours(d.planned)} esperadas`
            : undefined;
          return (
            <div
              key={i}
              title={tooltip}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 8,
                height: "100%",
                opacity: d.isFuture ? 0.4 : 1,
              }}
            >
              <div
                style={{
                  width: "100%",
                  flex: 1,
                  minHeight: 0,
                  position: "relative",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "var(--kr-neutral-100)",
                    borderRadius: 6,
                  }}
                />
                {/* Esperadas (gris medio) */}
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: `${plannedPct}%`,
                    background: "var(--kr-neutral-200)",
                    borderRadius: 6,
                  }}
                />
                {/* Trabajadas (acción) */}
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: `${workedPct}%`,
                    background: "var(--kr-action)",
                    borderRadius: 6,
                  }}
                />
              </div>
              <span
                style={{
                  fontSize: 11,
                  color: "var(--kr-fg-muted)",
                  fontFamily: "var(--kr-font-mono)",
                }}
              >
                {d.label}
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function PendingRequestsCard({ isManager, loading, pendingAbsences, pendingOvertime, employees }) {
  const navigate = useNavigate();

  const items = useMemo(() => {
    if (!isManager) return [];
    const empById = new Map(
      (employees || []).map((e) => [
        e.id,
        { name: `${e.first_name || ""} ${e.last_name || ""}`.trim() || "—" },
      ]),
    );
    const mapped = [
      ...(pendingAbsences || []).map((a) => ({
        kind: "absence",
        id: a.id,
        employeeProfileId: a.employee_profile_id,
        employeeName: empById.get(a.employee_profile_id)?.name || "—",
        type: ABSENCE_TYPE_LABEL[a.type] || a.type,
        detail: `${a.days_count} día${Number(a.days_count) === 1 ? "" : "s"} · ${formatDateRange(a.start_date, a.end_date)}`,
        sortKey: a.start_date,
        href: "/solicitudes",
      })),
      ...(pendingOvertime || []).map((o) => ({
        kind: "overtime",
        id: o.id,
        employeeProfileId: o.employee_profile_id,
        employeeName: empById.get(o.employee_profile_id)?.name || "—",
        type: "Horas extra",
        detail: `+${formatMinutes(o.minutes_requested)}`,
        sortKey: o.requested_at,
        href: "/solicitudes",
      })),
    ];
    mapped.sort((a, b) => (a.sortKey < b.sortKey ? 1 : -1));
    return mapped;
  }, [isManager, pendingAbsences, pendingOvertime, employees]);

  const totalCount = items.length;
  const visibleItems = items.slice(0, 4);

  if (!isManager) {
    return (
      <Card>
        <div
          style={{
            fontSize: 11,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: "var(--kr-fg-muted)",
            fontWeight: 600,
            marginBottom: 4,
          }}
        >
          Mis solicitudes
        </div>
        <EmptyState
          icon="Inbox"
          title="Sin solicitudes para revisar"
          description="Esta sección está disponible para managers."
        />
      </Card>
    );
  }

  const headline = totalCount === 0
    ? "Sin pendientes"
    : `${totalCount} esperando aprobación`;

  return (
    <Card>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 6,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "var(--kr-fg-muted)",
              fontWeight: 600,
            }}
          >
            Solicitudes pendientes
          </div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: "var(--kr-fg-strong)",
              marginTop: 4,
            }}
          >
            {loading ? "…" : headline}
          </div>
        </div>
        {totalCount > 0 && (
          <Button variant="ghost" size="sm" iconRight="ArrowRight" onClick={() => navigate("/solicitudes")}>
            Ver todas
          </Button>
        )}
      </div>
      {loading ? (
        <div style={{ padding: 24, textAlign: "center" }}><Spinner size={16} /></div>
      ) : visibleItems.length === 0 ? (
        <EmptyState
          icon="CheckCircle2"
          title="Todo al día"
          description="No hay solicitudes pendientes en este momento."
        />
      ) : (
        <div style={{ marginTop: 8, display: "flex", flexDirection: "column" }}>
          {visibleItems.map((it, i) => (
            <div
              key={it.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 0",
                borderBottom:
                  i < visibleItems.length - 1 ? "1px solid var(--kr-border-subtle)" : "none",
              }}
            >
              <EmployeeAvatar employeeId={it.employeeProfileId} hasAvatar={true} name={it.employeeName} size={32} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    color: "var(--kr-fg-strong)",
                    fontSize: 13,
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {it.employeeName}
                </div>
                <div style={{ color: "var(--kr-fg-muted)", fontSize: 12 }}>
                  {it.type} · {it.detail}
                </div>
              </div>
              <Button variant="secondary" size="sm" onClick={() => navigate(it.href)}>
                Revisar
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function TodayInOfficeCard({ loading, employees, todayStatus, absencesToday }) {
  const navigate = useNavigate();

  // Construye la lista anotada con estado de hoy. Empleados activos
  // primero; orden working → paused → finished → absent.
  const list = useMemo(() => {
    const absentSet = new Set((absencesToday || []).map((a) => a.employee_profile_id));
    const ordered = (employees || [])
      .filter((e) => (e.status || "active") === "active")
      .map((e) => {
        const st = todayStatus?.[e.id];
        let state;
        if (absentSet.has(e.id)) state = "absent";
        else if (st?.is_active) state = "working";
        else if (st && st.entries_count > 0) state = "finished";
        else state = "off";
        return {
          id: e.id,
          name: `${e.first_name || ""} ${e.last_name || ""}`.trim() || "—",
          position: e.position || null,
          totalSeconds: Number(st?.total_seconds || 0),
          state,
        };
      });
    const order = { working: 0, paused: 1, finished: 2, absent: 3, off: 4 };
    ordered.sort((a, b) => order[a.state] - order[b.state]);
    return ordered;
  }, [employees, todayStatus, absencesToday]);

  const workingCount = list.filter((p) => p.state === "working").length;
  const absentCount = list.filter((p) => p.state === "absent").length;
  const visible = list.slice(0, 8);

  const stateBadge = {
    working: <Badge tone="success" dot>Trabajando</Badge>,
    paused: <Badge tone="warning" dot>En pausa</Badge>,
    finished: <Badge tone="info" dot>Finalizada</Badge>,
    absent: <Badge tone="neutral" dot>Ausente</Badge>,
    off: <Badge tone="neutral" dot>Sin marcar</Badge>,
  };

  return (
    <Card>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "var(--kr-fg-muted)",
              fontWeight: 600,
            }}
          >
            Hoy en la empresa
          </div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: "var(--kr-fg-strong)",
              marginTop: 4,
            }}
          >
            {loading ? "Cargando…" : `${workingCount} trabajando · ${absentCount} ausentes`}
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconRight="ArrowRight"
          onClick={() => navigate("/empleados")}
        >
          Ver equipo
        </Button>
      </div>
      {loading ? (
        <div style={{ padding: 24, textAlign: "center" }}>
          <Spinner size={16} />
        </div>
      ) : visible.length === 0 ? (
        <EmptyState
          icon="Users"
          title="Sin empleados"
          description="Aún no hay empleados activos en este workspace."
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {visible.map((p, i) => (
            <div
              key={p.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 0",
                borderBottom:
                  i < visible.length - 1 ? "1px solid var(--kr-border-subtle)" : "none",
              }}
            >
              <EmployeeAvatar
                employeeId={p.id}
                hasAvatar={true}
                name={p.name}
                size={28}
                status={p.state === "working" ? "working" : p.state === "paused" ? "paused" : null}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    color: "var(--kr-fg-strong)",
                    fontSize: 13,
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {p.name}
                </div>
                {p.position && (
                  <div style={{ color: "var(--kr-fg-muted)", fontSize: 11 }}>
                    {p.position}
                  </div>
                )}
              </div>
              <span
                style={{
                  fontFamily: "var(--kr-font-mono)",
                  fontSize: 12,
                  color: "var(--kr-fg-strong)",
                  fontVariantNumeric: "tabular-nums",
                  minWidth: 56,
                  textAlign: "right",
                }}
              >
                {p.totalSeconds > 0 ? fmtHm(p.totalSeconds) : "—"}
              </span>
              {stateBadge[p.state]}
            </div>
          ))}
          {list.length > visible.length && (
            <div style={{ paddingTop: 10, fontSize: 12, color: "var(--kr-fg-muted)" }}>
              +{list.length - visible.length} más
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function fmtHm(totalSeconds) {
  const total = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")} h`;
}

/**
 * Lista de documentos internos con lectura obligatoria que el actor aún
 * no ha acusado. Sustituye el mock anterior de "Incidencias" hasta que
 * exista un endpoint real de incidencias (entradas sin cerrar, marcajes
 * fuera de horario, etc.).
 */
function PendingReadingsCard({ loading, docs }) {
  const navigate = useNavigate();
  const visible = (docs || []).slice(0, 5);
  return (
    <Card>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "var(--kr-fg-muted)",
              fontWeight: 600,
            }}
          >
            Lecturas obligatorias pendientes
          </div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: "var(--kr-fg-strong)",
              marginTop: 4,
            }}
          >
            {loading
              ? "Cargando…"
              : (docs?.length || 0) === 0
                ? "Sin pendientes"
                : `${docs.length} ${docs.length === 1 ? "documento" : "documentos"} por leer`}
          </div>
        </div>
        {(docs?.length || 0) > 0 && (
          <Button
            variant="ghost"
            size="sm"
            iconRight="ArrowRight"
            onClick={() => navigate("/documentacion-interna")}
          >
            Ver todos
          </Button>
        )}
      </div>
      {loading ? (
        <div style={{ padding: 24, textAlign: "center" }}>
          <Spinner size={16} />
        </div>
      ) : visible.length === 0 ? (
        <EmptyState
          icon="CheckCircle2"
          title="Todo leído"
          description="No tienes documentos internos pendientes de acusar."
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {visible.map((d, i) => (
            <div
              key={d.id}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                padding: "12px 0",
                borderBottom:
                  i < visible.length - 1 ? "1px solid var(--kr-border-subtle)" : "none",
              }}
            >
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: "var(--kr-status-warning-bg)",
                  color: "var(--kr-warning-700)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon name="BookOpen" size={14} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    color: "var(--kr-fg-strong)",
                    fontSize: 13,
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {d.title}
                </div>
                <div style={{ color: "var(--kr-fg-muted)", fontSize: 12 }}>
                  {d.code}
                  {d.published_at ? ` · publicado ${formatRelativeDays(d.published_at)}` : ""}
                </div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate("/documentacion-interna")}
              >
                Leer
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function formatRelativeDays(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return "hoy";
  if (days === 1) return "ayer";
  if (days < 30) return `hace ${days} días`;
  const months = Math.floor(days / 30);
  if (months === 1) return "hace 1 mes";
  return `hace ${months} meses`;
}

export default Dashboard;
